/**
 * Bottom-right pane — renders the result of the last `Send` click for the
 * currently-selected endpoint.
 *
 * Three visual states:
 *   1. `proxy_status === 'ok'` (regardless of gateway 2xx/4xx/5xx) — the
 *      emulator successfully reached Gateway and got a response. We show
 *      status code (color-coded), elapsed ms, signing applied, and the
 *      raw gateway_body as pretty-printed JSON.
 *   2. `proxy_status === 'gateway_unreachable'` — network / timeout /
 *      malformed JSON. Red diagnostic card with kind + message.
 *   3. `proxy_status === 'bad_request'` — emulator-side validation
 *      (e.g. missing path param). Yellow diagnostic card.
 */
import type { ProxyResult } from '@/lib/types';
import { isProxyOk } from '@/lib/types';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge, statusToBadgeVariant } from './ui/badge';

interface Props {
  result: ProxyResult | null;
}

function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function ResponseViewer({ result }: Props) {
  if (result === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Response</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 italic">
            Send a request to see the live Gateway response here.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!isProxyOk(result)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>Response</span>
            <Badge variant={result.proxy_status === 'gateway_unreachable' ? 'danger' : 'warning'}>
              {result.proxy_status}
            </Badge>
            <Badge variant="muted">{result.error_kind}</Badge>
            <span className="ml-auto text-xs font-mono text-slate-500">{result.elapsed_ms} ms</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="text-xs font-mono text-slate-500 break-all">
            {result.gateway_method} {result.gateway_url}
          </div>
          <div
            className={
              'rounded-md p-3 text-sm ' +
              (result.proxy_status === 'gateway_unreachable'
                ? 'bg-red-50 border border-red-200 text-red-900'
                : 'bg-amber-50 border border-amber-200 text-amber-900')
            }
          >
            {result.error_message}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <span>Response</span>
          <Badge variant={statusToBadgeVariant(result.gateway_status_code)}>
            HTTP {result.gateway_status_code}
          </Badge>
          {result.signing_applied && <Badge variant="info">signed: {result.signing_applied}</Badge>}
          {result.idempotency_key && (
            <Badge variant="muted" title={`Idempotency-Key: ${result.idempotency_key}`}>
              idem: {result.idempotency_key.slice(0, 8)}…
            </Badge>
          )}
          {result.idempotent_replayed === true && (
            <Badge
              variant="info"
              title={
                'Gateway returned idempotent-replayed: true — this response came from the ' +
                '24h cache for a previously-seen Idempotency-Key + body hash. The handler ' +
                'was NOT re-executed. See DOC_INCONSISTENCIES.md R5 / Issue #12.'
              }
            >
              🔁 replayed
            </Badge>
          )}
          <span className="ml-auto text-xs font-mono text-slate-500">{result.elapsed_ms} ms</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-xs font-mono text-slate-500 break-all">
          {result.gateway_method} {result.gateway_url}
        </div>
        <pre className="bg-slate-900 text-slate-100 rounded-md p-3 text-xs font-mono overflow-x-auto max-h-[400px] overflow-y-auto">
          {prettyJson(result.gateway_body)}
        </pre>
      </CardContent>
    </Card>
  );
}
